import React, { useState, useEffect, useCallback } from "react";
import { GoldenSourceHelper } from "../../../app/Switch_Core_React_Standards";
import { fetchCompanyTaxonomy, setCompanyTaxonomy } from "../companyTaxonomyInteractionService";
import {
  fetchTaxonomyHeaders,
  syncIngestedTaxonomyToSql,
} from "../../site-admin/components/Workflow-esg/services/EsgTaxonomySyncService";
import { ingestTaxonomy } from "../../site-admin/components/Workflow-esg/services/SustinaXbrlProcessorService";

/**
 * 🎯 COMPANY TAXONOMY PANEL
 * One active XBRL taxonomy per company (Corp.Z_08_CompanyTaxonomyBinding) —
 * either connect to an already-synced taxonomy version, or generate a
 * bespoke one from a taxonomy entry-point URL, scoped to this company's own
 * S3 output key. Single-file panel, mirrors ManageStructure.jsx's shape
 * (toggled in from CorporateDetailsView, not a separate route).
 */
export default function CompanyTaxonomyPanel({ companyId, companyName }) {
  const [binding, setBinding] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [entryPointUrl, setEntryPointUrl] = useState("");
  const [selectedHeaderId, setSelectedHeaderId] = useState("");
  const [error, setError] = useState("");
  const [genStatus, setGenStatus] = useState("");

  const siteKey = process.env.REACT_APP_SITE_KEY;

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const [currentBinding, allHeaders] = await Promise.all([
        fetchCompanyTaxonomy(companyId, { siteKey }),
        fetchTaxonomyHeaders({ siteKey }),
      ]);
      setBinding(currentBinding);
      setHeaders(allHeaders);
    } finally {
      setLoading(false);
    }
  }, [companyId, siteKey]);

  useEffect(() => {
    load();
  }, [load]);

  const boundHeaderId = GoldenSourceHelper.get(binding, "HeaderId");

  const boundHeaderLabel = () => {
    if (!binding) return null;
    const match = headers.find((h) => GoldenSourceHelper.get(h, "HeaderId") === boundHeaderId);
    return match
      ? `${GoldenSourceHelper.get(match, "VersionLabel")} (${GoldenSourceHelper.get(match, "TaxYear")})`
      : boundHeaderId;
  };

  const handleConnectExisting = async () => {
    if (!selectedHeaderId) return;
    setConnecting(true);
    setError("");
    try {
      await setCompanyTaxonomy(companyId, { headerId: selectedHeaderId, isBespoke: false }, { siteKey });
      await load();
    } catch (err) {
      setError(err.message || "Could not connect this taxonomy.");
    } finally {
      setConnecting(false);
    }
  };

  const handleGenerateBespoke = async () => {
    if (!entryPointUrl) return;
    setGenerating(true);
    setError("");
    try {
      setGenStatus("Ingesting taxonomy from entry point...");
      const ingestResult = await ingestTaxonomy({
        entryPointPath: entryPointUrl,
        outputKey: `taxonomies/company-${companyId}/concept-catalogue.json`,
      });

      if (!ingestResult?.success) {
        throw new Error(ingestResult?.message || "Taxonomy ingest failed.");
      }

      setGenStatus("Syncing concepts to Sustina...");
      const data = ingestResult.data ?? ingestResult;
      const { headerId } = await syncIngestedTaxonomyToSql(
        { bucket: data.bucket, key: data.key, versionLabel: `company-${companyId}-bespoke` },
        { siteKey }
      );

      await setCompanyTaxonomy(
        companyId,
        { headerId, isBespoke: true, sourceEntryPointUrl: entryPointUrl },
        { siteKey }
      );

      setEntryPointUrl("");
      await load();
    } catch (err) {
      setError(err.message || "Could not generate a bespoke taxonomy.");
    } finally {
      setGenStatus("");
      setGenerating(false);
    }
  };

  return (
    <div className="card shadow-sm border-0 bg-white overflow-hidden animate__animated animate__fadeIn">
      <div className="p-3 border-bottom bg-light">
        <h6 className="fw-bold extra-small text-uppercase text-primary mb-0">
          <i className="bi bi-diagram-2 me-1"></i> Taxonomy — {companyName || "Selected Entity"}
        </h6>
        <span className="extra-small text-muted">
          The ESG/XBRL taxonomy this company's disclosures and supplier classifications are measured against.
        </span>
      </div>

      <div className="p-3">
        {loading ? (
          <p className="text-muted extra-small italic mb-0">Loading...</p>
        ) : binding ? (
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div>
              <span className="badge bg-dark text-white extra-small me-2">
                {GoldenSourceHelper.get(binding, "IsBespoke") ? "BESPOKE" : "CONNECTED"}
              </span>
              <span className="fw-bold small">{boundHeaderLabel()}</span>
            </div>
          </div>
        ) : (
          <p className="text-muted extra-small italic">No taxonomy connected yet.</p>
        )}

        {error && <p className="text-danger extra-small mb-3">{error}</p>}

        <div className="row g-3">
          <div className="col-md-6 border-end">
            <h6 className="extra-small fw-bold text-uppercase text-muted mb-2">Connect Existing</h6>
            <select
              className="form-select form-select-sm mb-2"
              value={selectedHeaderId}
              onChange={(e) => setSelectedHeaderId(e.target.value)}
            >
              <option value="">-- Select a taxonomy version --</option>
              {headers.map((h, i) => (
                <option key={GoldenSourceHelper.get(h, "HeaderId") || i} value={GoldenSourceHelper.get(h, "HeaderId")}>
                  {GoldenSourceHelper.get(h, "VersionLabel")} ({GoldenSourceHelper.get(h, "TaxYear")})
                </option>
              ))}
            </select>
            <button
              className="btn btn-sm btn-outline-dark fw-bold"
              onClick={handleConnectExisting}
              disabled={connecting || !selectedHeaderId}
            >
              {connecting ? "CONNECTING..." : "Connect"}
            </button>
          </div>

          <div className="col-md-6">
            <h6 className="extra-small fw-bold text-uppercase text-muted mb-2">Generate Bespoke</h6>
            <input
              type="text"
              className="form-control form-control-sm mb-2"
              placeholder="Taxonomy entry-point URL (.xsd or .zip)"
              value={entryPointUrl}
              onChange={(e) => setEntryPointUrl(e.target.value)}
            />
            <button
              className="btn btn-sm btn-primary fw-bold"
              onClick={handleGenerateBespoke}
              disabled={generating || !entryPointUrl}
            >
              {generating ? "GENERATING..." : "Generate for this company"}
            </button>
            {genStatus && <p className="extra-small text-muted mt-2 mb-0">{genStatus}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
